
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	Dialog,
	FormattedText,
	Anchor,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./MobileAppDialog.module.less";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { AlertManager, useAlerts } from "client/Hooks";
import { getMobileAppInfo, MobileAppWellknown } from "client/Api/Wellknown/wellknown";

export type MobileAppDialogProps = {
	onClose(): void;
};

export const MobileAppDialog = (props: MobileAppDialogProps): ReactElement => {
	const {
		onClose
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [appInfo, setAppInfo] = useState<MobileAppWellknown>();

	const alertManager: AlertManager = useAlerts();
	const qrCodeUrl: string = useMemo(
		(): string => getMobileAppQrCodeUrl(),
		[]
	);

	useEffect(
		() => {
			async function loadAppInfo(signal: AbortSignal) {
				try {
					setIsLoading(true);
					const result: MobileAppWellknown = await getMobileAppInfo(signal);
					setAppInfo(result);
					setIsLoading(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoading(false);
				}
			}
			const cancellation: AbortController = new AbortController();
			loadAppInfo(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	return (
		<Dialog
			trackingId="mobile-app-dialog"
			hasBusyIndicator={isLoading}
		>
			<Dialog.Header
				title={localize("_MobileApp")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<div className={styles.title}>
					<h4>{localize("_ActivationSteps")}</h4>
				</div>
				<ol className={styles.list}>
					<li>
						<FormattedText
							text={localize("_DownloadWorkspaceAppInfo")}
							thingsToReplace={[
								{
									what: "{{GooglePlayStore}}",
									filler: (
										<Anchor
											href={appInfo?.android.downloadUrl}
											target="_blank"
										>
											{localize("_GooglePlayStore")}
										</Anchor>
									)
								},
								{
									what: "{{AppleAppStore}}",
									filler: (
										<Anchor
											href={appInfo?.iOs.downloadUrl}
											target="_blank"
										>
											{localize("_AppleAppStore")}
										</Anchor>
									)
								}
							]}
						/>
					</li>
					<li>{localize("_OpenTheAppDescription")}</li>
				</ol>
				<div className={styles.imageWrapper}>
					<img
						className={styles.image}
						src={qrCodeUrl}
					/>
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

MobileAppDialog.displayName = "MobileAppDialog";

function getMobileAppQrCodeUrl(): string {
	return createUrlRelativeToEnvironment(
		"/MobileApp/Onboarding/GetMobileAppQrCode"
	);
}

export function useMobileAppDialog(): [
	element: ReactElement | null,
	open: () => void
] {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const openHandler = useCallback(
		(): void => {
			setIsOpen(true);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setIsOpen(false);
		},
		[]
	);

	const renderDialog = (): ReactElement | null => {
		if (!isOpen) {
			return null;
		};

		return <MobileAppDialog onClose={closeHandler} />;
	};

	return [
		renderDialog(),
		openHandler
	];
}